'use client';

import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Backend integration point: replace with campaign analytics API
const performanceData = [
  { date: '14 Jul', email: 8, whatsapp: 12, retargeting: 2 },
  { date: '15 Jul', email: 14, whatsapp: 18, retargeting: 4 },
  { date: '16 Jul', email: 11, whatsapp: 9, retargeting: 3 },
  { date: '17 Jul', email: 19, whatsapp: 22, retargeting: 6 },
  { date: '18 Jul', email: 13, whatsapp: 14, retargeting: 4 },
  { date: '19 Jul', email: 7, whatsapp: 8, retargeting: 2 },
  { date: '20 Jul', email: 15, whatsapp: 17, retargeting: 5 },
];

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-elevated border border-border rounded-lg p-3 shadow-card-hover min-w-[160px]">
        <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
        {payload.map((p) => (
          <div key={`perf-tt-${p.name}`} className="flex justify-between gap-4 text-xs">
            <span className="text-muted-foreground">{p.name}</span>
            <span className="font-semibold font-tabular" style={{ color: p.color }}>
              {p.value} conv.
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function CampaignPerformanceChart() {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Conversiones por Campaña
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Comparativa de las 3 campañas activas — últimos 7 días
          </p>
        </div>
        <div className="flex items-center gap-3">
          {[
            { key: 'legend-email', label: 'Email', color: 'bg-primary' },
            { key: 'legend-wa', label: 'WhatsApp', color: 'bg-accent' },
            { key: 'legend-re', label: 'Retargeting', color: 'bg-warning' },
          ].map((l) => (
            <span key={l.key} className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <span className={`w-2.5 h-2.5 rounded-sm ${l.color}`} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={performanceData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="emailGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="waGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="reGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--warning)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--warning)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="date"
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="email"
            name="Email"
            stroke="var(--primary)"
            fill="url(#emailGrad)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="whatsapp"
            name="WhatsApp"
            stroke="var(--accent)"
            fill="url(#waGrad)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="retargeting"
            name="Retargeting"
            stroke="var(--warning)"
            fill="url(#reGrad)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}